import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Check, ChevronLeft, ChevronRight, Loader2, CalendarDays, Home, Sparkles,
  User, CreditCard, MapPin,
} from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { api, apiErr } from "../lib/api";
import { PageHero } from "../components/common";
import { COMPANY } from "../lib/constants";

const STEPS = ["Service", "Date & Time", "Your Home", "Add-ons", "Details & Pay"];
const pad = (n) => String(n).padStart(2, "0");
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export default function BookCleaning() {
  const [settings, setSettings] = useState(null);
  const [step, setStep] = useState(0);
  const [serviceId, setServiceId] = useState("");
  const [dateObj, setDateObj] = useState(null);
  const [windows, setWindows] = useState([]);
  const [loadingWindows, setLoadingWindows] = useState(false);
  const [timeWindow, setTimeWindow] = useState("");
  const [home, setHome] = useState({ address: "", bedrooms: 1, bathrooms: 1, sqft: 1000 });
  const [addons, setAddons] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [contact, setContact] = useState({ name: "", phone: "", email: "" });
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/settings/public").then((r) => setSettings(r.data)); }, []);

  const dateStr = dateObj ? fmt(dateObj) : "";
  useEffect(() => {
    if (!dateStr) return;
    setLoadingWindows(true); setTimeWindow(""); setWindows([]);
    api.get(`/bookings/availability`, { params: { date: dateStr } })
      .then((r) => setWindows(r.data.available))
      .catch(() => setWindows([]))
      .finally(() => setLoadingWindows(false));
  }, [dateStr]);

  // live quote
  useEffect(() => {
    if (!serviceId) { setQuote(null); return; }
    const t = setTimeout(() => {
      api.post("/bookings/quote", {
        service_id: serviceId, bedrooms: +home.bedrooms || 0, bathrooms: +home.bathrooms || 0,
        sqft: +home.sqft || 0, addon_ids: addons,
      }).then((r) => setQuote(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [serviceId, home, addons]);

  const disabledDays = useMemo(() => {
    if (!settings) return [];
    const blocked = (settings.blocked_dates || []).map((d) => new Date(d + "T00:00:00"));
    return [
      { before: new Date() },
      (d) => !(settings.available_days || []).includes((d.getDay() + 6) % 7), // JS Sun=0 -> Mon=0
      ...blocked,
    ];
  }, [settings]);

  const toggleAddon = (id) =>
    setAddons((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  const canNext = () => {
    if (step === 0) return !!serviceId;
    if (step === 1) return !!dateStr && !!timeWindow;
    if (step === 2) return home.address.trim().length > 3;
    return true;
  };

  const submit = async () => {
    if (!contact.name || !contact.phone || !contact.email) { toast.error("Please provide your name, phone and email."); return; }
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        service_id: serviceId, date: dateStr, time_window: timeWindow, address: home.address,
        bedrooms: +home.bedrooms || 0, bathrooms: +home.bathrooms || 0, sqft: +home.sqft || 0,
        addon_ids: addons, special_instructions: instructions,
        name: contact.name, phone: contact.phone, email: contact.email,
      });
      if (booking.amount_due_now > 0) {
        const { data } = await api.post("/payments/checkout", {
          booking_id: booking.id, origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
      } else {
        window.location.href = "/payment/success?booking_id=" + booking.id;
      }
    } catch (e) {
      toast.error(apiErr(e));
      setSubmitting(false);
    }
  };

  if (!settings) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-primary" size={36} /></div>;

  const services = settings.services || [];
  const addonList = settings.addons || [];
  const inputCls = "w-full border border-gray-300 rounded-md px-4 py-2.5 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors";
  const labelCls = "block text-sm font-semibold text-charcoal-700 mb-1.5";
  const payLabel = { full: "Full payment due at booking", deposit_fixed: "Deposit due now", deposit_percent: "Deposit due now", after_service: "Pay after service" }[quote?.payment_mode] || "";

  return (
    <div data-testid="book-page">
      <PageHero eyebrow="Book a Cleaning" title="Book your residential cleaning"
        subtitle="Pick a service, choose an available time, tell us about your home, and pay securely online." />

      <section className="bg-[#f8f9fa]">
        <div className="ubm-container py-14 grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2">
            {/* Stepper */}
            <div className="flex items-center justify-between mb-8 overflow-x-auto">
              {STEPS.map((s, i) => (
                <div key={s} className="flex items-center shrink-0">
                  <div className={`flex items-center gap-2 ${i <= step ? "text-primary" : "text-gray-400"}`}>
                    <div className={`w-8 h-8 rounded-full grid place-items-center text-sm font-semibold border-2 ${i < step ? "bg-primary border-primary text-white" : i === step ? "border-primary" : "border-gray-300"}`}>
                      {i < step ? <Check size={16} /> : i + 1}
                    </div>
                    <span className="text-xs font-semibold hidden sm:block">{s}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`w-6 sm:w-10 h-0.5 mx-1 ${i < step ? "bg-primary" : "bg-gray-300"}`} />}
                </div>
              ))}
            </div>

            <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} className="bg-white border border-gray-200 rounded-md p-6 sm:p-8">
              {step === 0 && (
                <div data-testid="step-service">
                  <h2 className="text-xl font-semibold text-charcoal-900 mb-1 flex items-center gap-2"><Sparkles className="text-primary" size={20} /> Select your cleaning service</h2>
                  <p className="text-sm text-charcoal-500 mb-5">Choose the service that fits your needs.</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {services.map((s) => (
                      <button key={s.id} onClick={() => setServiceId(s.id)} data-testid={`svc-${s.id}`}
                        className={`text-left border rounded-md p-4 transition-colors ${serviceId === s.id ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-gray-200 hover:border-primary"}`}>
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-charcoal-900">{s.name}</span>
                          <span className="text-primary font-bold text-sm">${Number(s.base_price).toFixed(0)}+</span>
                        </div>
                        <p className="text-xs text-charcoal-500 mt-1">{s.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div data-testid="step-datetime">
                  <h2 className="text-xl font-semibold text-charcoal-900 mb-1 flex items-center gap-2"><CalendarDays className="text-primary" size={20} /> Choose a date & time</h2>
                  <p className="text-sm text-charcoal-500 mb-5">Only available days can be selected. Times fill up, so book early!</p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="border border-gray-200 rounded-md p-2 w-fit">
                      <Calendar mode="single" selected={dateObj} onSelect={setDateObj} disabled={disabledDays} data-testid="booking-calendar" />
                    </div>
                    <div>
                      <div className={labelCls}>Available time windows {dateStr && `for ${dateStr}`}</div>
                      {!dateStr && <p className="text-sm text-charcoal-500">Select a date to see available times.</p>}
                      {loadingWindows && <Loader2 className="animate-spin text-primary" />}
                      {dateStr && !loadingWindows && windows.length === 0 && <p className="text-sm text-red-500">No availability on this day. Please pick another date.</p>}
                      <div className="grid grid-cols-1 gap-2 mt-1">
                        {windows.map((w) => (
                          <button key={w} onClick={() => setTimeWindow(w)} data-testid={`slot-${w}`}
                            className={`border rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${timeWindow === w ? "border-primary bg-primary text-white" : "border-gray-200 text-charcoal-800 hover:border-primary"}`}>
                            {w}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="step-home">
                  <h2 className="text-xl font-semibold text-charcoal-900 mb-1 flex items-center gap-2"><Home className="text-primary" size={20} /> Tell us about your home</h2>
                  <p className="text-sm text-charcoal-500 mb-5">This helps us bring the right team and give an accurate estimate.</p>
                  <div className="space-y-4">
                    <div><label className={labelCls}>Property Address *</label><input className={inputCls} value={home.address} onChange={(e) => setHome({ ...home, address: e.target.value })} data-testid="home-address" placeholder="123 Main St, Bentonville, AR" /></div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><label className={labelCls}>Bedrooms</label><input type="number" min="0" className={inputCls} value={home.bedrooms} onChange={(e) => setHome({ ...home, bedrooms: e.target.value })} data-testid="home-bedrooms" /></div>
                      <div><label className={labelCls}>Bathrooms</label><input type="number" min="0" className={inputCls} value={home.bathrooms} onChange={(e) => setHome({ ...home, bathrooms: e.target.value })} data-testid="home-bathrooms" /></div>
                      <div><label className={labelCls}>Approx. Sq Ft</label><input type="number" min="0" className={inputCls} value={home.sqft} onChange={(e) => setHome({ ...home, sqft: e.target.value })} data-testid="home-sqft" /></div>
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div data-testid="step-addons">
                  <h2 className="text-xl font-semibold text-charcoal-900 mb-1 flex items-center gap-2"><Sparkles className="text-primary" size={20} /> Optional add-ons</h2>
                  <p className="text-sm text-charcoal-500 mb-5">Add any extras your home needs.</p>
                  <div className="grid sm:grid-cols-2 gap-2.5">
                    {addonList.map((a) => (
                      <button key={a.id} onClick={() => toggleAddon(a.id)} data-testid={`addon-${a.id}`}
                        className={`flex items-center justify-between border rounded-md px-4 py-3 text-sm transition-colors ${addons.includes(a.id) ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-gray-200 hover:border-primary"}`}>
                        <span className="font-medium text-charcoal-800 flex items-center gap-2">
                          <span className={`w-4 h-4 rounded border grid place-items-center ${addons.includes(a.id) ? "bg-primary border-primary" : "border-gray-300"}`}>{addons.includes(a.id) && <Check size={12} className="text-white" />}</span>
                          {a.name}
                        </span>
                        <span className="text-primary font-semibold">+${Number(a.price).toFixed(0)}</span>
                      </button>
                    ))}
                  </div>
                  <div className="mt-5">
                    <label className={labelCls}>Special Instructions</label>
                    <textarea rows={3} className={inputCls} value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="instructions" placeholder="Gate codes, pets, focus areas, etc." />
                  </div>
                </div>
              )}

              {step === 4 && (
                <div data-testid="step-details">
                  <h2 className="text-xl font-semibold text-charcoal-900 mb-1 flex items-center gap-2"><User className="text-primary" size={20} /> Your contact information</h2>
                  <p className="text-sm text-charcoal-500 mb-5">We'll send your confirmation here.</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2"><label className={labelCls}>Full Name *</label><input className={inputCls} value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name" /></div>
                    <div><label className={labelCls}>Phone *</label><input className={inputCls} value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="contact-phone" /></div>
                    <div><label className={labelCls}>Email *</label><input type="email" className={inputCls} value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email" /></div>
                  </div>
                </div>
              )}

              {/* Nav buttons */}
              <div className="flex justify-between mt-8">
                <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                  className="inline-flex items-center gap-1.5 text-charcoal-700 font-semibold px-5 py-2.5 rounded-md hover:bg-gray-100 disabled:opacity-40 transition-colors" data-testid="back-btn">
                  <ChevronLeft size={18} /> Back
                </button>
                {step < STEPS.length - 1 ? (
                  <button onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Please complete this step.")}
                    className="inline-flex items-center gap-1.5 bg-primary text-white hover:bg-primary-hover font-semibold px-6 py-2.5 rounded-md transition-colors" data-testid="next-btn">
                    Continue <ChevronRight size={18} />
                  </button>
                ) : (
                  <button onClick={submit} disabled={submitting}
                    className="inline-flex items-center gap-2 bg-primary text-white hover:bg-primary-hover font-semibold px-6 py-2.5 rounded-md transition-colors disabled:opacity-60" data-testid="pay-btn">
                    {submitting ? <><Loader2 className="animate-spin" size={18} /> Processing…</> : <><CreditCard size={18} /> {quote?.amount_due_now > 0 ? `Pay $${quote.amount_due_now.toFixed(2)} & Book` : "Confirm Booking"}</>}
                  </button>
                )}
              </div>
            </motion.div>
          </div>

          {/* Summary */}
          <div className="lg:sticky lg:top-28">
            <div className="bg-white border border-gray-200 rounded-md p-6" data-testid="booking-summary">
              <h3 className="font-semibold text-charcoal-900 text-lg mb-4">Booking Summary</h3>
              <SummaryRow label="Service" value={quote?.service_name || "—"} />
              <SummaryRow label="Date" value={dateStr || "—"} />
              <SummaryRow label="Time" value={timeWindow || "—"} />
              <SummaryRow label="Home" value={`${home.bedrooms} bd · ${home.bathrooms} ba · ${home.sqft} sqft`} />
              {quote?.addons?.length > 0 && (
                <div className="py-2 border-t border-gray-100 mt-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Add-ons</div>
                  {quote.addons.map((a) => (
                    <div key={a.id} className="flex justify-between text-sm text-charcoal-700 py-0.5"><span>{a.name}</span><span>+${a.price.toFixed(0)}</span></div>
                  ))}
                </div>
              )}
              <div className="border-t border-gray-200 mt-3 pt-3 space-y-1">
                <div className="flex justify-between text-sm text-charcoal-600"><span>Estimated Total</span><span className="font-semibold text-charcoal-900">${quote ? quote.total.toFixed(2) : "0.00"}</span></div>
                {quote && quote.amount_due_now !== quote.total && (
                  <>
                    <div className="flex justify-between text-sm text-primary"><span>Due Now</span><span className="font-semibold">${quote.amount_due_now.toFixed(2)}</span></div>
                    <div className="flex justify-between text-sm text-charcoal-500"><span>Balance at Service</span><span>${quote.balance.toFixed(2)}</span></div>
                  </>
                )}
                {payLabel && <div className="text-xs text-charcoal-400 pt-1">{payLabel}</div>}
              </div>
              <div className="mt-4 flex items-start gap-2 text-xs text-charcoal-500">
                <MapPin size={14} className="text-primary mt-0.5 shrink-0" /> Payments are processed securely by Stripe. We never store your card details. Call {COMPANY.phoneDisplay} with any questions.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-1.5"><span className="text-charcoal-500">{label}</span><span className="font-medium text-charcoal-900 text-right max-w-[60%] truncate">{value}</span></div>
  );
}
